// Program copy for /inner-circle. Keep claims to what the founder has
// approved: no recordings, named speakers or financial
// results until they are confirmed.

export const PAGE_PATH = "/inner-circle";
export const PAGE_TITLE = "Postaway Inner Circle · From Posting to Profit";
export const PAGE_DESCRIPTION =
  "A hands-on social media experience for hosts and content creators. 12 live sessions on Google Meet and 12 weekly challenges, January 20 – April 21, 2027.";

// Mirrors ALLOW_BOTH_ROLE in api/_lib/innerCircle.mjs.
export const ALLOW_BOTH_ROLE = false;

// Scarcity wording stays off until a numeric capacity is approved.
export const SHOW_LIMITED_SPOTS = false;

export const FALLBACK_PRICES = { memberPriceCents: 59900, regularPriceCents: 79900, currency: "usd" };

export const DATES_LABEL = "January 20 – April 21, 2027";
export const CADENCE_LABEL = "Weekly on Wednesdays";
export const SESSION_TIME_LABEL = "1–2:30 p.m. CST";

export const AUDIENCE = [
  {
    title: "Hosts",
    text: "You have professional photos and videos of your property, but not the confidence, ideas or routine to keep using them between creator collaborations.",
  },
  {
    title: "Creators",
    text: "You want practical learning, real connections and the accountability to keep showing up for your audience and your partners.",
  },
];

export const BENEFITS = [
  { title: "12 live sessions", text: "Live guidance on Google Meet, one topic at a time, built around what hosts and creators actually post." },
  { title: "12 weekly challenges", text: "A posting challenge every week so ideas turn into published content, not a list you never get to." },
  { title: "Practical tips", text: "Hooks, shot lists, editing, batching and tools you can put to work the same week." },
  { title: "Guest contributions", text: "Perspectives from people in the hospitality and creator world, alongside the live sessions." },
  { title: "Supportive community", text: "A private GroupMe community for questions, feedback, wins and accountability between sessions." },
];

export const OUTCOMES = [
  "More confidence showing up on social media",
  "A usable library of content for your brand or property",
  "A 90-day content strategy you can keep running after the program",
];

// Complete calendar schedule (all dates 2027). Breaks are not sessions.
export const SCHEDULE = [
  { session: 1, date: "Jan 20", iso: "2027-01-20", title: "The Instagram First Impression", text: "Your brand, your bio and your why." },
  { session: 2, date: "Jan 27", iso: "2027-01-27", title: "Content That Converts", text: "Hooks, storytelling and calls to action that drive results." },
  { session: 3, date: "Feb 3", iso: "2027-02-03", title: "Lights. Camera. Content.", text: "Property photography, B-roll and shot lists." },
  { session: 4, date: "Feb 10", iso: "2027-02-10", title: "The Art of the Scroll-Stop", text: "Reels, editing and video storytelling." },
  { break: true, date: "Feb 17", iso: "2027-02-17", title: "Take a Break", text: "No live session." },
  { session: 5, date: "Feb 24", iso: "2027-02-24", title: "Style It. Shoot It. Share It.", text: "Styling, details and lifestyle content that stands out." },
  { session: 6, date: "Mar 3", iso: "2027-03-03", title: "From Posting to Profit", text: "Affiliate programs, storefronts and Shop Your Stay." },
  { session: 7, date: "Mar 10", iso: "2027-03-10", title: "Your Content Goldmine", text: "Batching, repurposing and building your content library." },
  { break: true, date: "Mar 17", iso: "2027-03-17", title: "Take a Break", text: "No live session." },
  { session: 8, date: "Mar 24", iso: "2027-03-24", title: "Beyond the Property", text: "Itineraries, local guides, lead magnets and growing your audience." },
  { session: 9, date: "Mar 31", iso: "2027-03-31", title: "The Collaboration Playbook", text: "Pitches, deliverables and partnerships that work." },
  { session: 10, date: "Apr 7", iso: "2027-04-07", title: "From Followers to Bookings", text: "Converting engagement into inquiries and sales." },
  { session: 11, date: "Apr 14", iso: "2027-04-14", title: "Work Smarter. Post Better.", text: "Tools, AI, analytics and optimizing what works." },
  { session: 12, date: "Apr 21", iso: "2027-04-21", title: "The Next Chapter", text: "Your 90-day content strategy and next steps." },
];

export const buildFaqs = ({ memberPrice, regularPrice, supportEmail }) => [
  {
    q: "Who is Inner Circle for?",
    a: "Vacation rental hosts and content creators who want a guided, hands-on way to plan, create and post with purpose.",
  },
  {
    q: "Do I need a Postaway membership to join?",
    a: `No. Anyone can join at the regular tuition of ${regularPrice}. Postaway members pay ${memberPrice}. To get the member rate, register with the email address on your Postaway account.`,
  },
  {
    q: "Is this a subscription?",
    a: "No. Tuition is a single payment for this cohort with no automatic renewal. Joining Inner Circle doesn't add, change or cancel a Postaway membership.",
  },
  {
    q: "How are sessions delivered?",
    a: `Sessions are held live on Google Meet, ${SESSION_TIME_LABEL}. Joining details are shared with enrolled participants.`,
  },
  {
    q: "Where does the community live?",
    a: "On GroupMe. After your payment is confirmed, your welcome email includes a private GroupMe invitation along with your next steps.",
  },
  {
    q: "When does it run?",
    a: `${CADENCE_LABEL}, ${SESSION_TIME_LABEL}, ${DATES_LABEL}. There are no live sessions on February 17 or March 17.`,
  },
  {
    q: "I already registered. How do I get my welcome email again?",
    a: `Use “Resend my welcome email” in the registration section, or contact ${supportEmail}.`,
  },
];

export const ROLE_OPTIONS = [
  { id: "host", label: "Host", hint: "I host guests at one or more properties" },
  { id: "creator", label: "Creator", hint: "I create content for hospitality brands" },
  ...(ALLOW_BOTH_ROLE ? [{ id: "both", label: "Both", hint: "I host and I create content" }] : []),
];

export const REFERRAL_OPTIONS = [
  { id: "instagram", label: "Instagram" },
  { id: "postaway_member", label: "Postaway member" },
  { id: "referral", label: "Host/creator referral" },
  { id: "event", label: "Summit/event" },
  { id: "other", label: "Other" },
];

export const formatMoney = (cents, currency = "usd") => {
  if (typeof cents !== "number") return "";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: String(currency || "usd").toUpperCase(),
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch (_) {
    return `$${(cents / 100).toFixed(0)}`;
  }
};
